// Нижний лист выбора из списка (сезон, формат). Закрывается тапом по фону, системной «Назад» Telegram и Escape.
import { type ReactNode, useEffect, useRef } from 'react';
import caretDown from '../assets/icons/caret-down.svg';
import { haptic } from '../telegram/webapp';
import { MaskIcon } from './MenuCell';
import { useDialogFocus } from './useDialogFocus';
import { useOverlayBack } from './useOverlayBack';
import styles from './Sheet.module.css';

/** Открытые окна (сверху — последнее): Escape, как и «Назад», закрывает только верхнее. */
const escapeStack: Array<() => void> = [];

/** Окно поверх экрана (HQ-1371): системная «Назад» Telegram (стек bindOverlayBack) и Escape закрывают его, а не уводят
 *  с экрана; лист поверх листа — первое нажатие закрывает верхний. После закрытия кнопка — как была у экрана. */
export function useOverlayDismiss(onClose: () => void): void {
  useOverlayBack(onClose);
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    const close = () => ref.current();
    escapeStack.push(close);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || escapeStack[escapeStack.length - 1] !== close) return;
      e.preventDefault();
      close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      const i = escapeStack.lastIndexOf(close);
      if (i >= 0) escapeStack.splice(i, 1);
      window.removeEventListener('keydown', onKey);
    };
  }, []);
}

/** То же обёрткой: окно-вид остаётся без своих хуков (дерево ReactionsSheet тесты разбирают вызовом функции). */
export function OverlayDismiss({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useOverlayDismiss(onClose);
  return <>{children}</>;
}

export function PickerSheet<T extends string | number>({ title, options, selected, onSelect, onClose }: {
  title: string; options: Array<{ value: T; label: string }>; selected: T | undefined;
  onSelect: (value: T) => void; onClose: () => void;
}) {
  useOverlayDismiss(onClose);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef);
  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div ref={dialogRef} className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.grabber} />
        <div className={styles.title}>{title}</div>
        <div className={styles.list}>
          {options.map((o) => (
            <button key={o.value} className={styles.option} onClick={() => { haptic.selection(); onSelect(o.value); }}>
              <span>{o.label}</span>{o.value === selected && <span className={styles.check}>✓</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Чип выбора (сезон): активен, только если есть из чего выбирать. */
export function PickerChip({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button className={styles.chip} onClick={onClick} disabled={!onClick}>
      {label}{onClick && <span className={styles.chipIcon}><MaskIcon src={caretDown} size={24} /></span>}
    </button>
  );
}

/** Нижний лист-пояснение: заголовок, текст и одна кнопка закрытия (подписка оформлена — как отменить). */
export function InfoSheet({ title, text, close, onClose }: { title: string; text: string; close: string; onClose: () => void }) {
  useOverlayDismiss(onClose);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef);
  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div ref={dialogRef} className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.grabber} />
        <div className={styles.title}>{title}</div>
        <p className={styles.text}>{text}</p>
        <button className={styles.close} onClick={onClose}>{close}</button>
      </div>
    </div>
  );
}
